import { blogApiBase } from '../config';
import { getRefresh, setTokens, clear } from './tokenStore';

function authUrl(path: string): string {
  return `${blogApiBase()}/clients/new_dawn/auth${path}`;
}

interface TokenPair {
  access_token: string;
  refresh_token: string;
}

// Backend rejected the Google identity (not the practice owner). Distinct from
// network/other failures so the UI can show the right message.
export class AuthRejectedError extends Error {
  constructor() {
    super('Not authorised');
    this.name = 'AuthRejectedError';
  }
}

// Exchange a Google ID token for a backend access+refresh pair.
export async function loginWithGoogle(idToken: string): Promise<void> {
  const res = await fetch(authUrl('/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (res.status === 401 || res.status === 403) throw new AuthRejectedError();
  if (!res.ok) throw new Error(`Login failed: ${res.status}`);
  const { access_token, refresh_token } = (await res.json()) as TokenPair;
  setTokens({ access: access_token, refresh: refresh_token });
}

// Single-flight: concurrent callers (boot + an in-progress 401) share one request,
// so token rotation can't invalidate a sibling refresh.
let inFlight: Promise<boolean> | null = null;

export function refreshTokens(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doRefresh(): Promise<boolean> {
  const refresh = getRefresh();
  if (!refresh) {
    clear();
    return false;
  }
  let res: Response;
  try {
    res = await fetch(authUrl('/refresh'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refresh }),
    });
  } catch {
    return false; // transient network error: keep tokens, surface as a failed call
  }
  if (!res.ok) {
    clear(); // refresh token rejected (expired/revoked) -> force re-login
    return false;
  }
  const { access_token, refresh_token } = (await res.json()) as TokenPair;
  setTokens({ access: access_token, refresh: refresh_token });
  return true;
}
